/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import Moment from 'react-moment';

import { setTradeOrdersPeriod, setDataIsLoading } from '../actions/load';
import { fetchTradeOrders, resetTradeOrdersPagination } from '../actions/actionsSet/order';

import FlexBox from './FlexBox';
import EmptyResult from './EmptyResult';
import ScrollableTable from './ScrollableTable';
import ProTableFilters from './ProTableFilters';
import SelfOtherPairsToggler from './SelfOtherPairsToggler';
import StopLimitTrigger from './StopLimitTrigger';
import LoadingShade from './LoadingShade';

import withI18n from '../utils/withI18n';
import { formatNumber, DASH } from '../utils/number';
import { replace as i18nReplace, getLabel } from '../utils/i18n';
import withErrorHandler from '../utils/withErrorHandler';
import type { RowGetter, CellDataGetter, CellRenderer } from '../utils/dataTypes';

const CAPITALIZE = { 'textTransform': 'capitalize' };
const DEFAULT_ROW = 200;
const ROW_HEIGHT = 30;
const HEADER_HEIGHT = 30;
const DEFAULT_WIDTH = 1260;
const PRO_DEFAULT_WIDTH = 1333;

const Label = styled.span`
  display: flex;
  align-items: center;
  flex-direction: column;
`;
const GreenLabel = styled(Label)`
  color: ${({ theme }) => theme.up};
`;
const RedLabel = styled(Label)`
  color: ${({ theme }) => theme.down};
`;

const Container = styled(FlexBox)`
  flex: 1 0 100%;
  min-height: ${({ isLoggedIn, pro }) => {
    if (pro) return '100%';
    return isLoggedIn ? '250px' : '29px';
  }};
  position: relative;
`;

const Wrapper = styled(FlexBox)`
  flex: 1 0 100%;
  position: relative;
  height: ${({ pro }) => (pro ? 'calc(100% - 30px)' : '100%')};
  .ReactVirtualized__Table__headerColumn,
  .ReactVirtualized__Table__rowColumn {
    margin-right: 7px;
  }
  .ReactVirtualized__Table__headerColumn:first-of-type,
  .ReactVirtualized__Table__rowColumn:first-of-type {
    margin-left: 7px;
  }
  .grey {
    color: ${({ theme }) => theme.fontColor2};
  }
`;

const ALIGN_RIGHT = { justifyContent: 'flex-end', paddingRight: '1em' };
const ALIGN_CENTER = { justifyContent: 'center' };

const HeaderRowRenderer = styled(FlexBox)`
  border-bottom: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.fontColor3};
  font-weight: normal;
  line-height: 1;
  width: 100%;
`;

const RowRenderer = styled(FlexBox)`
  border-bottom: 1px solid ${({ theme }) => theme.border};
  cursor: pointer;
`;

const mapStateToProps = ({
  isLoggedIn,
  tickSizes,
  minTrades,
  locale,
  tradeOrdersPagination,
  tradeOrdersPeriod,
  isDataLoading,
}) => ({
  isLoggedIn,
  tickSizes,
  minTrades,
  locale,
  tradeOrdersPagination,
  tradeOrdersPeriod,
  isDataLoadingTradeOrders: isDataLoading.tradeOrders,
});

const mapDispatchToProps = {
  fetchTradeOrders, resetTradeOrdersPagination, setTradeOrdersPeriod, setDataIsLoading,
};

type Props = {
  symbol: string,
  locale?: string,
  base?: string,
  i18n: Object,
  isLoggedIn?: boolean,
  tradeOrders: Array<Object>,
  tickSizes: Object,
  minTrades: Object,
  pro: boolean,
  fetchTradeOrders: Function,
  isDataLoadingTradeOrders: boolean,
  setDataIsLoading: Function,
  tradeOrdersPagination?: Object,
  setTradeOrdersPeriod?: Function,
  resetTradeOrdersPagination: Function,
  tradeOrdersPeriod?: string,
  isMargin: boolean,
};

type State = { hideOtherPairs: boolean, start: number };
type TableProps = { columns?: React.Node };

export default
@withI18n
@withErrorHandler
@connect(
  mapStateToProps,
  mapDispatchToProps
)
class OpenOrdersTable extends React.Component<Props, State> {
  static defaultProps = {
    i18n: {},
    base: '',
    locale: '',
    isLoggedIn: false,
    tradeOrders: [],
    tickSizes: {},
    minTrades: {},
    pro: false,
    fetchTradeOrders: () => {},
    setDataIsLoading: () => {},
    isDataLoadingTradeOrders: false,
    tradeOrdersPagination: {},
    setTradeOrdersPeriod: () => {},
    resetTradeOrdersPagination: () => {},
    tradeOrdersPeriod: '1d',
    isMargin: false,
  };

  state = {
    hideOtherPairs: false,
    start: -1,
  };

  componentDidMount() {
    /* eslint-disable */
      this.setState({
        hideOtherPairs: JSON.parse(sessionStorage.getItem('tradeOrderHideOtherPairs') || 'false'),
      });
  }

  async componentDidUpdate(prevProps:Props): any {
    const { pro, isMargin,resetTradeOrdersPagination }=this.props;
    if(pro && prevProps.isMargin !== isMargin) {
         await resetTradeOrdersPagination()
        this._loadData(false)
    }
  }

  _getTradeOrders = () =>
    this.props.tradeOrders.filter(item => {
      const { start } = this.state;
      // https://sentry.io/binance-holdings/exchange-ui-pages/issues/580301102
      if (!item) return false; // TODO: why is it not defined?
      if (item.time < start) return false; // local time filter
      if (this.state.hideOtherPairs) return item.symbol === this.props.symbol;
      return true;
    });

  _rowGetter = ({ index }: RowGetter) => this._getTradeOrders()[index];

  _headerRowRenderer = (props: TableProps) => (
    <HeaderRowRenderer {...props}>{props.columns}</HeaderRowRenderer>
  );

  _rowRenderer = (props: TableProps) => <RowRenderer {...props}>{props.columns}</RowRenderer>;

  _noRowsRenderer = (props: Props) => {
    const { hideOtherPairs } = this.state;
    const { i18n, symbol, pro } = this.props;
    let { base } = this.props;
    if (!base) base = '';
    const message = hideOtherPairs
      ? i18nReplace(
          [`${base}/${symbol.replace(base, '')}`],
          getLabel('exchange-TradeOrdersTable-orderHistoryFor', i18n)
        )
      : getLabel('exchange-TradeOrdersTable-noOrderHistory', i18n);
    return <EmptyResult {...props} message={message} pro={pro} />;
  };

  _onCheckboxClick = () => {
    sessionStorage.setItem('tradeOrderHideOtherPairs', JSON.stringify(!this.state.hideOtherPairs));
    this.setState({ hideOtherPairs: !this.state.hideOtherPairs });
  };

  _cellRendererTrigger = ({ cellData }: CellRenderer) => {
    const { tickSizes } = this.props;
    return StopLimitTrigger({ tickSizes, cellData });
  };

  _onFilterChange = (start: number, end: number, bool: boolean, period: string) => {
    const { isMargin }=this.props
    this.setState({ start });
    if (!this.props.setTradeOrdersPeriod) return;
    bool ? this.props.setTradeOrdersPeriod(period) : this.props.setTradeOrdersPeriod('');
    const { locale } = this.props;
    if (!this.props.fetchTradeOrders) return;
    this.props.setDataIsLoading({ key: 'tradeOrders', isLoading: true });
    this.props
      .fetchTradeOrders(
        {
          startTime: start,
          endTime: end,
          page: 1,
          rows: DEFAULT_ROW,
        },
        locale,
        false,
       isMargin
      )
      .then(() => {
        this.props.setDataIsLoading({ key: 'tradeOrders', isLoading: false });
      });
  };

  _loadData = (bottom:boolean) => {
    const { locale, tradeOrdersPagination } = this.props;
    if (!tradeOrdersPagination || !this.props.fetchTradeOrders) return;
    const { startTime, endTime, pages } = tradeOrdersPagination;
    const page = tradeOrdersPagination.page + 1;
    if (bottom && page > pages) return;
    this.props.fetchTradeOrders(
      {
        startTime,
        endTime,
        page,
        rows: DEFAULT_ROW,
      },
      locale,
      true
    );
  };

  render() {
    const {
      i18n,
      isLoggedIn,
      tickSizes,
      minTrades,
      pro,
      symbol,
      tradeOrdersPeriod,
      isDataLoadingTradeOrders,
      isMargin,
      locale
    } = this.props;
    return (
      <Container bg="theme" isLoggedIn={isLoggedIn} pro={pro}>
        <SelfOtherPairsToggler
          id="order-toggler"
          i18n={i18n}
          pro={pro}
          readMore={true}
          marginStatus={isMargin}
          onClick={this._onCheckboxClick}
          checked={this.state.hideOtherPairs}
          textKey={''}
          locale={locale}
        />
        {pro ? (
          <ProTableFilters
            symbol={symbol}
            period={tradeOrdersPeriod}
            onFilterChange={this._onFilterChange}
          />
        ) : null}
        <Wrapper pro={pro}>
          <LoadingShade hidden={!isDataLoadingTradeOrders} />
          <ScrollableTable
            defaultWidth={pro ? PRO_DEFAULT_WIDTH : DEFAULT_WIDTH}
            defaultHeight={isLoggedIn ? undefined : 0}
            headerHeight={HEADER_HEIGHT}
            rowHeight={ROW_HEIGHT}
            rowCount={this._getTradeOrders().length}
            rowGetter={this._rowGetter}
            rowRenderer={this._rowRenderer}
            noRowsRenderer={this._noRowsRenderer}
            nonce={typeof window !== 'undefined' && window.__NONCE__}
            scrollable={isLoggedIn}
            contentOverflowVisible={!isLoggedIn && !pro}
            isBottom={this._loadData}
          >
            <ScrollableTable.Column
              label={getLabel('exchange-TradeOrdersTable-Date', i18n)}
              dataKey="transactTime"
              width={122}
              cellDataGetter={({ rowData }: CellDataGetter) =>
                rowData && (rowData.transactTime || rowData.time)
              }
              cellRenderer={({ cellData }: CellRenderer) => (
                <Moment element="span" format="MM-DD HH:mm:ss">
                  {cellData}
                </Moment>
              )}
            />
            <ScrollableTable.Column
              label={getLabel('exchange-TradeOrdersTable-Pair', i18n)}
              dataKey="symbol"
              width={100}
              cellDataGetter={({ rowData }: CellDataGetter) =>
                rowData && `${rowData.baseAsset}/${rowData.quoteAsset}`
              }
            />
            <ScrollableTable.Column
              label={getLabel('exchange-TradeOrdersTable-Type', i18n)}
              dataKey="type"
              width={100}
              cellDataGetter={({ rowData }: CellDataGetter) => rowData && rowData.orderType}
            />
            <ScrollableTable.Column
              label={getLabel('exchange-TradeOrdersTable-Side', i18n)}
              dataKey="side"
              width={70}
              cellDataGetter={({ rowData }: CellDataGetter) => rowData && rowData.side}
              cellRenderer={({ cellData }: CellRenderer) =>
                cellData === 'BUY' ? (
                  <GreenLabel>{getLabel('exchange-TradeOrdersTable-Buy', i18n)}</GreenLabel>
                ) : (
                  <RedLabel>{getLabel('exchange-TradeOrdersTable-Sell', i18n)}</RedLabel>
                )
              }
              headerStyle={ALIGN_CENTER}
              style={ALIGN_CENTER}
            />
            <ScrollableTable.Column
              label={getLabel('exchange-TradeOrdersTable-Avg', i18n)}
              dataKey="executedPrice"
              width={122}
              cellDataGetter={({ rowData }: CellDataGetter) =>
                rowData && +rowData.executedPrice
                ? formatNumber(rowData.executedPrice, tickSizes[rowData.symbol])
                  : DASH
              }
            />
            <ScrollableTable.Column
              label={getLabel('exchange-TradeOrdersTable-Price', i18n)}
              dataKey="price"
              width={122}
              cellDataGetter={({ rowData }: CellDataGetter) =>
                rowData && Number(rowData.price) === 0
                  ? getLabel('exchange-TradeOrdersTable-marketPrice', i18n)
                  : formatNumber(rowData.price, tickSizes[rowData.symbol])
              }
              className="grey"
            />
            <ScrollableTable.Column
              label={getLabel('exchange-TradeOrdersTable-Filled', i18n)}
              dataKey="filled"
              width={90}
              cellDataGetter={({ rowData }: CellDataGetter) =>
                rowData && +rowData.executedQty
                  ? formatNumber(rowData.executedQty, minTrades[rowData.symbol])
                  : `${DASH}`
              }
            />
            <ScrollableTable.Column
              label={getLabel('exchange-common-amount', i18n)}
              dataKey="origQty"
              width={90}
              cellDataGetter={({ rowData }: CellDataGetter) =>
                rowData && formatNumber(rowData.origQty, minTrades[rowData.symbol])
              }
              className="grey"
            />
            <ScrollableTable.Column
              label={getLabel('exchange-TradeOrdersTable-Total', i18n)}
              dataKey="total"
              width={142}
              cellDataGetter={({ rowData }: CellDataGetter) =>
                rowData &&
                formatNumber(rowData.executedQuoteQty, tickSizes[rowData.symbol]) !== formatNumber(0, tickSizes[rowData.symbol])
                  ? formatNumber(rowData.executedQuoteQty, tickSizes[rowData.symbol])
                  : DASH
              }
              headerStyle={ALIGN_RIGHT}
              style={ALIGN_RIGHT}
            />
            <ScrollableTable.Column
              label={getLabel('exchange-TradeOrdersTable-TriggerConditions', i18n)}
              dataKey="condition"
              width={112}
              cellDataGetter={({ rowData }: CellDataGetter) => rowData}
              cellRenderer={this._cellRendererTrigger}
            />
            <ScrollableTable.Column
              label={getLabel('exchange-TradeOrdersTable-Status', i18n)}
              dataKey="status"
              width={112}
              style={CAPITALIZE}
              cellDataGetter={({ rowData }: CellDataGetter) =>
                rowData&&rowData.status&&rowData.status.toLowerCase()
              }
              cellRenderer={({ cellData }: CellRenderer) => cellData || 'Status'}
            />
          </ScrollableTable>
        </Wrapper>
      </Container>
    );
  }
}
